import { Fragment, useContext, useEffect, useState } from "react";
import { LayerInfo } from "src/common/managers/Types";
import { Localization } from "@viamap/viamap2-common";
import { SheetFunc } from "src/managers/SheetFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ExportData } from "src/managers/ExportData";
import { stringify } from 'wkt';
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { GlassButton } from "./GlassButtons";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput";
import { ManagedWindow } from "src/WindowManager/WindowState";

type exportType = "xlsx" | "geojson" | "layerInfo" | "styleJson"
type callbackEvent = "cancel" | "accept"


type Props = {
  managedWindow: ManagedWindow;
  layers?: LayerInfo | LayerInfo[]
  callBack: (evt: callbackEvent) => void
};

function layerHasGeoJsonFeatureTable(layer: LayerInfo): boolean {
  return Array.isArray(layer.geoJson?.features);
}

function defaultType(layer: LayerInfo): exportType {
  if (layer.type.includes("Point")) {
    return "xlsx"
  }
  return "geojson"
}

function availableTypes(layer: LayerInfo, debugFeature?: boolean): exportType[] {
  let types = [
    ...(layerHasGeoJsonFeatureTable(layer) || layer.type.includes("Point") ? ["xlsx"] : []),

    "geojson",
    "styleJson",
    ...(debugFeature ? ["layerInfo"] : []),
  ]
  return types as exportType[]
}

export function DownloadDialog(props: Props) {
  const { hasAccessToFeature } = useContext(ApplicationStateContext)
  const layers = !props.layers ? [] : "type" in props.layers ? [props.layers] : props.layers
  const [name, setName] = useState(layers.map((a) => a.datasetname))
  const [format, setFormat] = useState(layers.map((a) => defaultType(a)))

  useEffect(() => {
    setName(layers.map((a) => a.datasetname))
    setFormat(layers.map((a) => defaultType(a)))
  }, [props.layers])

  function updateName(value: string, idx: number) {
    setName((name) => name.map((a, idy) => idx === idy ? (value ?? "") : a))
  }

  function Accept() {
    layers.map((a, idx) => {
      const exportlayer: (LayerInfo & { export: exportType }) = { ...a, datasetname: name[idx], export: format[idx] }
      downloadSingleLayer(exportlayer)
    })
    props.callBack("accept")
  }

  function Cancel() {
    props.callBack("cancel")
  }


  return <AdvancedDragModal
    window={props.managedWindow}
    title={Localization.getText("Export")}
    variant="Info"
  >
    <ADModalBody>
      <GlassInputGroup
        autoFocus={true}
        onEscape={Cancel}
        onSubmit={Accept}
      >
        {layers.map((a, idx) => {
          const exportTypes = availableTypes(a, hasAccessToFeature(Feature.Debugging))

          return (
            <Fragment key={a.layerId ?? "Error"}>

              <GlassTextInputTransformer
                label={Localization.getText("Layer") + ": " + (idx + 1)}
              >
                <GlassInputControl>
                  <input type="text" value={name[idx]} placeholder={a.datasetname ?? "Error"} onChange={(ev) => updateName(ev.target.value, idx)} />
                  <select value={format[idx]} style={{ flex: "unset", width: "18ex", cursor: "pointer" }}
                    onChange={((a) => setFormat((sf) => sf.map((cur, idy) => idx === idy ? a.target.value as exportType || "geojson" : cur)))}
                  >
                    {exportTypes.map((a) => {
                      return (
                        <option key={a} value={a}>.{a}</option>
                      )
                    })}
                  </select>

                  {/* <div onClick={() => changeFormat(idx)}>{"."+format[idx]}</div> */}
                </GlassInputControl>
              </GlassTextInputTransformer>
            </Fragment>)
        })}
      </GlassInputGroup>
    </ADModalBody>
    <ADModalFooter>
      <GlassButton onClick={Accept}>{Localization.getText("Export")}</GlassButton>
      <GlassButton onClick={Cancel}>{Localization.getText("Cancel")}</GlassButton>
    </ADModalFooter>
  </AdvancedDragModal>
}


function downloadSingleLayer(layerInfo: (LayerInfo & { export: exportType })) {
  let blob: Blob | null = null
  let extension = ".xlsx"

  switch (layerInfo.export) {
    case "layerInfo":
      blob = new Blob([JSON.stringify(layerInfo)], { type: "application/json" })
      extension = ".layerinfo"
      break;
    case "styleJson":
      blob = new Blob([JSON.stringify({
        ...validGeoJson(layerInfo.geoJson)
        , "style": layerInfo.styling
      })], { type: "application/json" })
      extension = ".geojson"
      break;
    case "geojson":
      blob = new Blob([JSON.stringify(validGeoJson(layerInfo.geoJson))], { type: "application/json" })
      extension = ".geojson"
      break;
    case "xlsx": {
      const features = layerInfo.geoJson?.features ?? [];
      let sheet = SheetFunc.jsonToSheet(features.map((a) => geoJsonFeatureToExcelRow(a)));
      blob = SheetFunc.createBlob(SheetFunc.sheetToDownload(sheet));
      break;
    }
  }
  ExportData.downloadBlob((layerInfo.datasetname ?? "MapitDownload") + extension, blob)
}

/** One spreadsheet row per feature: properties as columns, plus Wkt (and Lat/Lng for points). */
function geoJsonFeatureToExcelRow(feature: { geometry?: { type?: string; coordinates?: number[] }; properties?: Record<string, unknown> }) {
  const properties = feature.properties ?? {};
  if (!feature.geometry?.type) {
    return properties;
  }
  if (feature.geometry.type === "Point") {
    const coords = feature.geometry.coordinates ?? [];
    return { ...properties, "Lat": coords[0], "Lng": coords[1], "Wkt": stringify(feature.geometry) };
  }
  return { ...properties, "Wkt": stringify(feature.geometry) };
}

// Currently only converts coords to numbers
function validGeoJson(geojson: any) {
  return GenerateGeomUtils.geoJsonCoordsTransformer(geojson, (coord) => {
    return coord.map((a) => Number(a)) as [number, number]
  })
}